import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChatCircleDots, CheckCircle } from '@phosphor-icons/react';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { createCollectionActivity } from '../../../../../../redux/services/collectionActivityService';
import { createPromiseToPay } from '../../../../../../redux/services/promiseToPayService';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { BuyerContact, TextMessageOutcome, CollectionActivityReference } from '@dill/dill-shared';
import LexicalEditor from '../../../../../general/LexicalEditor/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';
import {
  PromiseToPayFieldsSection,
  defaultPromiseToPayFieldsValue,
  isPromiseToPayFieldsValid,
  PromiseToPayFieldsValue,
} from '../PromiseToPayFieldsSection/PromiseToPayFieldsSection';

interface AddTextMessageModalProps {
  open: boolean;
  buyerId: string;
  phoneNumber?: string;
  sendingMode?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialReferences?: CollectionActivityReference[];
  contacts?: BuyerContact[];
}

type SendingStage = 'FORM' | 'SENDING' | 'DELIVERED';

const textMessageOutcomeOptions: { value: TextMessageOutcome; label: string }[] = [
  { value: 'DELIVERED', label: 'Delivered - Message was delivered' },
  { value: 'READ', label: 'Read - Message was read' },
  { value: 'NO_RESPONSE', label: 'No Response - No reply received' },
  { value: 'FAILED', label: 'Failed - Message could not be sent' },
];

function AddTextMessageModal({
  open,
  buyerId,
  phoneNumber = '',
  sendingMode = false,
  onClose,
  onSuccess,
  initialReferences,
  contacts,
}: AddTextMessageModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [phone, setPhone] = useState(phoneNumber);
  const [messageContent, setMessageContent] = useState('');
  const [outcome, setOutcome] = useState<TextMessageOutcome>('DELIVERED');
  const [notes, setNotes] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>(
    initialReferences ?? []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [promiseFields, setPromiseFields] = useState<PromiseToPayFieldsValue>(
    defaultPromiseToPayFieldsValue
  );

  // Sending mode state
  const [sendingStage, setSendingStage] = useState<SendingStage>('FORM');
  const [showFullForm, setShowFullForm] = useState(!sendingMode);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isLoading = loadingList.includes('createCollectionActivity');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPhone(phoneNumber);
      setMessageContent('');
      setOutcome('DELIVERED');
      setNotes('');
      setReferences([]);
      setPhoneError('');
      setMessageError('');
      setIsSubmitting(false);
      setSendingStage('FORM');
      setShowFullForm(!sendingMode);
      setPromiseFields(defaultPromiseToPayFieldsValue);
    }
  }, [open, phoneNumber]);

  useEffect(() => {
    if (open && initialReferences) {
      setReferences(initialReferences);
    }
  }, [open, initialReferences]);

  // Cleanup timeout on unmount or close
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleNotesChange = useCallback((content: string) => {
    setNotes(content);
  }, []);

  const buildActivityData = () => ({
    supplierId: currentSupplier?.id || '',
    buyerId,
    activityType: 'TEXT_MESSAGE' as const,
    source: 'MANUAL' as const,
    performedBy: user!.id,
    date: new Date(),
    textMessageDetails: {
      phoneNumber: phone,
      messageContent,
      outcome,
      notes: notes ? JSON.parse(notes) : undefined,
    },
    references: references.length > 0 ? references : undefined,
  });

  const handleSend = async () => {
    if (!phone.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    if (!messageContent.trim()) {
      setMessageError('Please enter the message content');
      return;
    }

    // Show sending animation
    setSendingStage('SENDING');

    // Simulate sending delay, then show delivered, then open log form
    setTimeout(() => {
      setSendingStage('DELIVERED');
      closeTimeoutRef.current = setTimeout(() => {
        setShowFullForm(true);
        setSendingStage('FORM');
      }, 1200);
    }, 1500);
  };

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    if (!messageContent.trim()) {
      setMessageError('Please enter the message content');
      return;
    }

    if (!currentSupplier?.id) {
      return;
    }

    if (!isPromiseToPayFieldsValid(promiseFields)) {
      dispatch(
        openMessageModal({
          title: 'Missing promise details',
          subTitle:
            'Please fill in the payment type, reason, and promised date, or uncheck "Customer promised to pay".',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        createCollectionActivity({
          supplierId: currentSupplier.id,
          activityData: buildActivityData(),
        })
      );

      if (result.payload && (result.payload as any).success) {
        if (promiseFields.logPromise) {
          try {
            await dispatch(
              createPromiseToPay({
                supplierId: currentSupplier.id,
                data: {
                  supplierId: currentSupplier.id,
                  buyerId,
                  source: 'TEXT',
                  type: promiseFields.type || null,
                  reason: promiseFields.reason || null,
                  reasonOther:
                    promiseFields.reason === 'OTHER' ? promiseFields.reasonOther || null : null,
                  promisedPaymentDate: new Date(promiseFields.promisedPaymentDate),
                  amount: promiseFields.amount ? parseFloat(promiseFields.amount) : null,
                  references: references.length > 0 ? references : null,
                  notes: null,
                  status: 'ACTIVE',
                },
              })
            );
          } catch (promiseError) {
            console.error('Failed to log promise to pay:', promiseError);
            dispatch(
              openMessageModal({
                title: 'Text logged, but promise to pay failed',
                subTitle:
                  'The text was logged successfully, but we could not save the promise to pay. Please add it manually.',
                buttonText: 'OK',
                modalType: 'ERROR',
              })
            );
          }
        }
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to log text message:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to log text message. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setPhone('');
    setMessageContent('');
    setOutcome('DELIVERED');
    setNotes('');
    setReferences([]);
    setPhoneError('');
    setMessageError('');
    setPromiseFields(defaultPromiseToPayFieldsValue);
    onClose();
  };

  const isAnimating = sendingStage === 'SENDING' || sendingStage === 'DELIVERED';

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title={showFullForm ? 'Log Text Message' : 'Send Text Message'}
      maxWidth="576px"
      closeOnOverlayClick={!isAnimating}
      footer={
        !isAnimating ? (
          <>
            <DillButton
              onClick={handleClose}
              variant="secondary"
              isDisabled={isLoading}
              label="Cancel"
            />
            <DillButton
              onClick={showFullForm ? handleSubmit : handleSend}
              variant="primary"
              isLoading={isLoading || isSubmitting}
              label={showFullForm ? 'Log Text' : 'Send'}
            />
          </>
        ) : undefined
      }>
      {/* SENDING stage */}
      {sendingStage === 'SENDING' && (
        <div className="flex flex-col items-center py-8">
          <div className="relative flex items-center justify-center w-24 h-24 mb-6">
            <div className="add-call-pulse-ring absolute inset-0 rounded-full bg-teal-400" />
            <div className="add-call-pulse-ring add-call-pulse-ring-delayed absolute inset-0 rounded-full bg-teal-400" />
            <div className="relative z-10 w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center">
              <ChatCircleDots size={28} weight="fill" className="text-white" />
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-800 mb-1">Sending...</p>
          {phone && <p className="text-sm text-gray-500 mb-2">{phone}</p>}
          <p className="text-xs text-gray-400 max-w-[240px] text-center mt-2 truncate">
            {messageContent}
          </p>
        </div>
      )}

      {/* DELIVERED stage */}
      {sendingStage === 'DELIVERED' && (
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
            <CheckCircle size={28} weight="fill" className="text-white" />
          </div>
          <p className="text-lg font-semibold text-emerald-600 mb-1">Message Delivered</p>
          {phone && <p className="text-sm text-gray-500">{phone}</p>}
        </div>
      )}

      {/* FORM stage */}
      {sendingStage === 'FORM' && (
        <div className="space-y-4">
          <AppInputField
            label="Phone Number"
            isRequired
            id="text-phone"
            value={phone}
            onTextChange={(val) => {
              setPhone(val);
              if (phoneError) setPhoneError('');
            }}
            placeholder="+1 (555) 123-4567"
            isDisabled={isLoading}
            errorText={phoneError}
          />

          {showFullForm && (
            <AppDropDown
              label="Outcome"
              items={textMessageOutcomeOptions}
              value={outcome}
              onSelectChange={(selected) => setOutcome(selected?.value as TextMessageOutcome)}
              isDisabled={isLoading}
              placeholder="Select outcome"
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={messageContent}
              onChange={(e) => {
                setMessageContent(e.target.value);
                if (messageError) setMessageError('');
              }}
              placeholder="Enter the text message content..."
              rows={4}
              disabled={isLoading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all resize-y"
            />
            {messageError && <p className="mt-1 text-xs text-red-500">{messageError}</p>}
          </div>

          {showFullForm && (
            <LexicalEditor
              label="Notes (Optional)"
              onChange={handleNotesChange}
              placeholder="Enter additional notes..."
              minHeight="100px"
              editable={!isLoading}
            />
          )}

          {showFullForm && (
            <ActivityReferenceSelector
              label="References (Optional)"
              buyerId={buyerId}
              value={references}
              onChange={setReferences}
              isDisabled={isLoading}
              contacts={contacts}
            />
          )}

          {showFullForm && (
            <PromiseToPayFieldsSection
              value={promiseFields}
              onChange={setPromiseFields}
              isDisabled={isLoading}
            />
          )}
        </div>
      )}
    </DillModal>
  );
}

export default AddTextMessageModal;
